import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import { stateOf as alertStateOf, stateLabel, typeOf } from "../components/statusAlerts";
import { features } from "../config/features";
import { attentionItems, healthNotices, healthOf, readRecent } from "../admin/health";
import {
  AdAvatar,
  HealthDot,
  Pill,
  Progress,
  ToastProvider,
  Empty,
} from "../admin/ui";
import {
  api,
  type AdminAnnouncementsPage,
  type AdminClientListItem,
  type FormAdmin,
  type KbAdminPage,
  type Me,
} from "../services/api";

/**
 * Admin home — the landing page for /admin.
 *
 * Replaces the old redirect-to-first-section index. Three jobs: find a
 * customer fast (⌘K search over name / domain / Autotask id), jump straight
 * to a task, and see what onboarding work is outstanding across every active
 * customer. Everything here reads; nothing on this page mutates.
 */
export function AdminHome() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());

  if (!me) {
    return <div className="card">Loading…</div>;
  }
  const isStaff = me.role === "iquest_admin" || me.role === "service_desk";
  if (!isStaff) {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">The admin area is for iQuest staff.</p>
      </div>
    );
  }
  return (
    <ToastProvider>
      <AdminHomeInner me={me} />
    </ToastProvider>
  );
}

function greeting(): string {
  const hour = Number(
    new Date().toLocaleString("en-AU", {
      hour: "numeric",
      hour12: false,
      timeZone: "Australia/Perth",
    }),
  );
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function AdminHomeInner({ me }: { me: Me }) {
  const navigate = useNavigate();
  const isIquestAdmin = me.role === "iquest_admin";

  // Customers are iquest_admin-only server-side, so a service_desk user gets
  // the search + quick actions without the customer panels rather than a 403.
  const { data: clients, error: clientsError } = useSWR<AdminClientListItem[]>(
    isIquestAdmin ? ["/api/admin/clients", "health"] : null,
    () => api.admin.clients.list({ include: "health" }),
  );
  const { data: alerts } = useSWR<AdminAnnouncementsPage>(
    ["/api/admin/announcements", "home"],
    () => api.announcements.adminList(1, 50),
  );
  const { data: forms } = useSWR<FormAdmin[]>("/api/admin/forms", () =>
    api.admin.forms.list({ include_unpublished: true }),
  );
  const { data: kbDrafts } = useSWR<KbAdminPage>(
    features.kb ? ["/api/admin/kb", "draft"] : null,
    () => api.adminKb.list({ status: "draft", page: 1, page_size: 1 }),
  );

  const list = useMemo(() => clients ?? [], [clients]);
  const attention = useMemo(() => attentionItems(list), [list]);
  const notices = useMemo(() => healthNotices(list), [list]);
  const healthClear =
    attention.length === 0 && notices.banner.length === 0 && notices.rows.length === 0;
  const issueCount = clientsError
    ? 1
    : attention.length + notices.rows.length + notices.banner.length;

  const openAlerts = useMemo(
    () => (alerts?.items ?? []).filter((a) => a.is_active),
    [alerts],
  );

  const recent = useMemo(() => {
    const ids = readRecent(me.id);
    const byId = new Map(list.map((c) => [c.id, c]));
    return ids.map((id) => byId.get(id)).filter((c): c is AdminClientListItem => !!c);
  }, [list, me.id]);

  const firstName = me.display_name.trim().split(/\s+/)[0] || me.display_name;

  return (
    <div className="rf-wrap">
      <div className="rf-pagehead">
        <div className="ad-eyebrow">Admin</div>
        <h1 className="rf-h1">
          {greeting()}, {firstName}
        </h1>
        <p className="rf-sub">
          Find a customer, or jump straight to the job you came here for.
        </p>
      </div>

      {isIquestAdmin && clientsError && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          The customer list could not be loaded, so search is unavailable. Status alerts
          and the other actions on this page still work.
        </div>
      )}
      {isIquestAdmin && !clientsError && <CustomerSearch clients={list} />}

      <div className="ad-quick">
        {isIquestAdmin && (
          <Link className="primary" to="/admin/clients/new">
            <span className="ic">
              <Icon name="plus" />
            </span>
            <span>
              <span className="t">New customer</span>
              <span className="s">From Autotask, ~3 minutes</span>
            </span>
          </Link>
        )}
        <Link to="/admin/announcements/new">
          <span className="ic">
            <Icon name="megaphone" />
          </span>
          <span>
            <span className="t">Post a status alert</span>
            <span className="s">
              {openAlerts.length} open right now
            </span>
          </span>
        </Link>
        <Link to="/admin/forms?new=1">
          <span className="ic">
            <Icon name="doc" />
          </span>
          <span>
            <span className="t">Build a form</span>
            <span className="s">{forms?.length ?? 0} in the catalogue</span>
          </span>
        </Link>
        {features.kb && (
          <Link to="/admin/kb/new">
            <span className="ic">
              <Icon name="book" />
            </span>
            <span>
              <span className="t">Write a KB article</span>
              <span className="s">{kbDrafts?.total ?? 0} drafts waiting</span>
            </span>
          </Link>
        )}
      </div>

      <div className="ad-home-grid">
        <div className="ad-card">
          <div className="ad-card-head">
            <span className="t">Needs attention</span>
            {isIquestAdmin && (
              <Pill kind={issueCount ? "warn" : "ok"}>
                {issueCount} item{issueCount === 1 ? "" : "s"}
              </Pill>
            )}
          </div>
          {!isIquestAdmin ? (
            <Empty
              title="Customer setup is iQuest-admin only"
              blurb="Status alerts, forms and the knowledge base are yours to run."
            />
          ) : clientsError ? (
            <div className="form-error" style={{ margin: 18 }}>
              Customer health could not be loaded.
            </div>
          ) : !clients ? (
            <div style={{ padding: 18 }}>Loading customers…</div>
          ) : (
            <>
              {notices.banner.map((message) => (
                <div key={message} className="form-error" style={{ margin: 18 }}>
                  {message}
                </div>
              ))}
              {notices.rows.map((row) => (
                <Link
                  key={row.id}
                  className="ad-attn ad-row"
                  to={`/admin/clients/${row.id}`}
                >
                  <span className="bd">
                    <span className="t">{row.name}</span>
                    <span className="s">{row.message}</span>
                  </span>
                  <span className="go">
                    <Icon name="chevRight" />
                  </span>
                </Link>
              ))}
              {healthClear ? (
                <Empty title="All customers are set up" blurb="Nothing is waiting on you." />
              ) : (
                attention.map((item) => (
                  <Link
                    key={`${item.client.id}-${item.section}`}
                    className="ad-attn ad-row"
                    to={`/admin/clients/${item.client.id}/${item.section}`}
                  >
                    <HealthDot state={item.state} />
                    <span className="bd">
                      <span className="t">
                        {item.client.name} · {item.title}
                      </span>
                      <span className="s">{item.blurb}</span>
                    </span>
                    <span className="go">
                      <Icon name="chevRight" />
                    </span>
                  </Link>
                ))
              )}
            </>
          )}
        </div>

        <div>
          {isIquestAdmin && (
            <div className="ad-card">
              <div className="ad-card-head">
                <span className="t">Recently worked on</span>
                <Link className="lnk" to="/admin/clients">
                  All customers <Icon name="arrowRight" />
                </Link>
              </div>
              {clientsError ? (
                <Empty
                  title="Customers could not be loaded"
                  blurb="Recently opened customers show up here once the list loads."
                />
              ) : recent.length === 0 ? (
                <Empty
                  title="Nothing opened yet"
                  blurb="Customers you open show up here."
                  action={
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate("/admin/clients")}
                    >
                      Browse customers
                    </button>
                  }
                />
              ) : (
                recent.map((c) => {
                  const h = healthOf(c);
                  return (
                    <Link key={c.id} className="ad-row" to={`/admin/clients/${c.id}`}>
                      <AdAvatar name={c.name} />
                      <span className="bd">
                        <span className="t">{c.name}</span>
                        <span className="s">
                          {c.user_count} user{c.user_count === 1 ? "" : "s"} ·{" "}
                          {c.tickets_30d == null && c.health_error
                            ? "ticket count unavailable"
                            : `${c.tickets_30d ?? 0} tickets / 30d`}
                        </span>
                      </span>
                      {c.setup_health ? (
                        <Progress done={h.done} total={h.total} />
                      ) : c.health_error ? (
                        <span className="s">Health unavailable</span>
                      ) : (
                        <Progress done={h.done} total={h.total} />
                      )}
                    </Link>
                  );
                })
              )}
            </div>
          )}

          <div className="ad-card" style={{ marginTop: isIquestAdmin ? 16 : 0 }}>
            <div className="ad-card-head">
              <span className="t">Status alerts</span>
              <Link className="lnk" to="/admin/announcements">
                Triage board <Icon name="arrowRight" />
              </Link>
            </div>
            {openAlerts.length === 0 ? (
              <Empty title="Nothing live" blurb="No open status alerts right now." />
            ) : (
              openAlerts.slice(0, 5).map((a) => {
                const state = alertStateOf(a);
                return (
                  <Link key={a.id} className="ad-row" to={`/admin/announcements/${a.id}`}>
                    <span className="bd">
                      <span className="t">{a.title}</span>
                      <span className="s">
                        {typeOf(a) === "outage"
                          ? "Outage"
                          : typeOf(a) === "maintenance"
                            ? "Maintenance"
                            : "Enhancement"}{" "}
                        ·{" "}
                        {a.target_all
                          ? "All customers"
                          : `${a.target_company_ids?.length ?? 0} customer${
                              (a.target_company_ids?.length ?? 0) === 1 ? "" : "s"
                            }`}
                      </span>
                    </span>
                    <Pill kind={state === "draft" ? "off" : "info"}>{stateLabel(state)}</Pill>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ⌘K-focusable search over customer name, allowed domains and Autotask id. */
function CustomerSearch({ clients }: { clients: AdminClientListItem[] }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return clients
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.slug.includes(q) ||
          String(c.autotask_company_id ?? "").includes(q),
      )
      .slice(0, 6);
  }, [clients, query]);

  return (
    <div className="ad-home-search">
      <Icon name="search" size={20} />
      <input
        ref={inputRef}
        value={query}
        placeholder="Search customers by name, slug or Autotask ID…"
        autoComplete="off"
        aria-label="Search customers"
        onChange={(e) => {
          setQuery(e.target.value);
          setSelected(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && hits.length) {
            e.preventDefault();
            setSelected((s) => (s + 1) % hits.length);
          } else if (e.key === "ArrowUp" && hits.length) {
            e.preventDefault();
            setSelected((s) => (s - 1 + hits.length) % hits.length);
          } else if (e.key === "Enter") {
            if (hits[selected]) navigate(`/admin/clients/${hits[selected].id}`);
            else if (query.trim()) navigate("/admin/clients/new");
          } else if (e.key === "Escape") {
            setQuery("");
          }
        }}
      />
      <kbd>⌘K</kbd>
      {query.trim() && (
        <div className="ad-results">
          {hits.length > 0 ? (
            hits.map((c, i) => (
              <Link
                key={c.id}
                className={`ad-result${i === selected ? " sel" : ""}`}
                to={`/admin/clients/${c.id}`}
                onMouseEnter={() => setSelected(i)}
              >
                <AdAvatar name={c.name} />
                <span className="n">{c.name}</span>
                <span className="m">
                  {c.autotask_company_id != null ? `AT ${c.autotask_company_id}` : "unlinked"}
                </span>
              </Link>
            ))
          ) : (
            <div className="ad-result">
              <span className="n" style={{ color: "var(--iq-text-muted)" }}>
                No customer matches “{query.trim()}”
              </span>
              <Link className="m" style={{ color: "var(--iq-violet)" }} to="/admin/clients/new">
                Create it →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
